/**
 * The catalogue's default form, per chart.
 *
 * `suggestTemplate` guesses a form from the shape of a response, which is the
 * right behaviour for a chart nobody has an opinion about. But the data team
 * DO have an opinion about these sixty — the Chart Queries API Integration
 * Guide ships a "Default viz" column — and their intent beats a heuristic that
 * only sees columns and rows.
 *
 * Keyed by the catalogue's `chart_id`. A chart that is not listed falls through
 * to the heuristic, so a new backend query renders sensibly the day it appears
 * rather than waiting for this file to be updated.
 *
 * ── Where this deviates from the guide, and why ──────────────────────────────
 * Every entry below was checked against the chart's real response on
 * 2026-08-31. Nine of them cannot draw the form the guide names, because the
 * query does not return the columns that form needs. Those are marked
 * `// deviation:` with the reason, and are collected in
 * `DEVIATIONS` for the data team — the fix is upstream in the query, not here.
 * Guessing a form the data cannot support would render an empty card, which
 * reads as "no data" when the truth is "wrong form".
 */

/** The guide's viz names, mapped to this app's templates. */
const AREA = 'trend';
const LINE = 'trend';
const BARH = 'bar';
const BAR = 'column';
const PIE = 'donut';
const BIGNUM = 'stat';
const TOP = 'ranked-columns';
const STACKED = 'stacked';
const TABLE = 'table';
const WORLDMAP = 'worldmap';

export const CHART_DEFAULTS: Record<string, string> = {
  1: TABLE, // deviation: guide says app-list; there is no such form, and all three columns are labels
  2: BARH,
  3: TOP,
  4: TOP,
  5: TOP,
  6: AREA,
  7: STACKED,
  8: AREA,
  9: AREA,
  10: TABLE,
  11: TABLE,
  12: TABLE,
  13: TABLE,
  14: WORLDMAP,
  15: TABLE,
  16: TABLE, // deviation: guide says barh; the query returns only `keyword` — no measure to draw
  17: TABLE, // deviation: as 16
  18: TABLE,
  19: TABLE, // deviation: guide says barh; the query returns only `feature_request`
  20: TABLE,
  21: TABLE, // deviation: guide says bignum; the value is the word "negative", and a stat tile needs a number
  22: TABLE,
  23: LINE,
  24: TABLE,
  25: TABLE,
  26: TABLE,
  27: TOP,
  28: TOP,
  29: TABLE,
  30: LINE,
  31: TABLE,
  32: TABLE,
  33: BIGNUM,
  34: BIGNUM,
  35: BIGNUM,
  36: BIGNUM,
  37: TABLE, // deviation: guide says pie; one row of pre-formatted strings, segments across columns not down rows
  38: BAR,
  39: TABLE, // deviation: guide says bignum; one row carrying three separate figures
  40: 'leaderboard', // guide says barh; ranked names with a magnitude track is the same picture, better drawn
  41: TABLE,
  42: TABLE, // deviation: as 37
  43: TABLE, // deviation: guide says bignum; four separate KPIs in one row
  44: TOP,
  45: TOP,
  46: TOP,
  47: TABLE,
  48: TABLE,
  49: TABLE, // deviation: guide says line; the query returns a pre-rendered polyline string, not points
  50: TABLE,
  51: BARH,
  52: BARH,
  53: LINE,
  54: LINE, // deviation: guide says stacked-bar; one average per month has no parts to stack
  55: LINE,
  56: TABLE,
  57: TABLE, // deviation: guide says bignum; Hero KPIs is ten figures in one row
  58: TOP,
  59: TOP,
  60: TOP,
};

/**
 * The charts whose data cannot support the form the guide specifies, for the
 * ticket that asks the data team to change the query.
 */
export const DEVIATIONS: Array<{ chartId: string; wanted: string; needs: string }> = [
  { chartId: '1', wanted: 'app-list', needs: 'a form that does not exist here yet' },
  { chartId: '16', wanted: 'barh', needs: 'a count/frequency column beside `keyword`' },
  { chartId: '17', wanted: 'barh', needs: 'a count/frequency column beside `keyword`' },
  { chartId: '19', wanted: 'barh', needs: 'a count column beside `feature_request`' },
  { chartId: '21', wanted: 'bignum', needs: 'a numeric score, not the word "negative"' },
  { chartId: '37', wanted: 'pie', needs: 'one row per segment rather than one row of columns' },
  { chartId: '39', wanted: 'bignum', needs: 'one figure per query, or a KPI-row form' },
  { chartId: '42', wanted: 'pie', needs: 'one row per platform rather than one row of columns' },
  { chartId: '43', wanted: 'bignum', needs: 'a KPI-row form; four figures share one row' },
  { chartId: '49', wanted: 'line', needs: 'the points as rows, not a pre-rendered polyline string' },
  { chartId: '54', wanted: 'stacked-bar', needs: 'sentiment split into parts, not one average' },
  { chartId: '57', wanted: 'bignum', needs: 'a KPI-row form; ten figures share one row' },
];

/** The form to draw chart `chartId` in, or undefined to fall back to the heuristic. */
export const defaultTemplate = (chartId: string): string | undefined =>
  CHART_DEFAULTS[String(chartId)];
